/**
 * @file ContentForm.tsx
 * @description Component for rendering the form where users input content details.
 * Manages form state and handles form submission and reset events.
 */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GenerateContentFormProps } from "../../types";
import { useUserDetails } from "../../context/UserDetailsContext";
import { useClients } from "../../hooks/useClients";
import { useWebsites } from "../../hooks/useWebsites";
import log from "../../config/logging";
import KeywordInput from "./KeywordInput";
import ResetIcon from "./icons/ResetIcon";
import PencilIcon from "./icons/PencilIcon";
import { useNavbarContext } from "../../context/NavbarContext";
import { validateUrl } from "../../utils/urlUtils";

interface ExtendedContentFormProps extends GenerateContentFormProps {
  variant: "create" | "edit";
  onReset?: () => void;
}

const ContentForm: React.FC<ExtendedContentFormProps> = ({
  keyword,
  setKeyword,
  website,
  setWebsite,
  contentType,
  setContentType,
  clientInfo,
  setClientInfo,
  relatedKeywords,
  setRelatedKeywords,
  peopleAlsoAsk,
  setPeopleAlsoAsk,
  title,
  setTitle,
  onSubmit,
  isLoading,
  variant,
  onReset,
}) => {
  const { userDetails, isAuthenticated, refreshUserDetails } = useUserDetails();
  const { openLoginDropdown } = useNavbarContext();
  const [websiteError, setWebsiteError] = useState<string>("");
  const [showKeywordError, setShowKeywordError] = useState<boolean>(false);
  const [peopleAlsoAskRaw, setPeopleAlsoAskRaw] = useState<string>("");
  
  // Hooks must always be called in the same order
  const { clients } = useClients();
  const [selectedClientId, setSelectedClientId] = useState<string>("");
  const { websites } = useWebsites(selectedClientId || undefined);
  const [selectedWebsiteId, setSelectedWebsiteId] = useState<string>("");
  
  const isPresetSelected = !!selectedWebsiteId;

  // Auto-detect preset when form data is loaded (e.g., when editing generated content)
  // First, try to find client by clientInfo
  useEffect(() => {
    if (clientInfo && !selectedClientId && clients.length > 0) {
      const matchingClient = clients.find((c) => c.name === clientInfo);
      if (matchingClient) {
        setSelectedClientId(matchingClient.id);
      }
    }
  }, [clientInfo, clients, selectedClientId]);

  // Once client is selected and websites are loaded, try to find matching website
  useEffect(() => {
    if (selectedClientId && website && contentType && !selectedWebsiteId && websites.length > 0) {
      const matchingWebsite = websites.find(
        (w) => w.website === website && w.contentType === contentType
      );
      
      if (matchingWebsite) {
        setSelectedWebsiteId(matchingWebsite.id);
      }
    }
  }, [selectedClientId, website, contentType, websites, selectedWebsiteId]);

  const isButtonDisabled =
    isLoading ||
    (isAuthenticated && (userDetails?.remainingContents ?? 0) <= 0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!isAuthenticated) {
      openLoginDropdown();
      return;
    }

    // Validate required fields
    if (!keyword || !keyword.trim()) {
      setShowKeywordError(true);
      log.warn("Form submission blocked: keyword is required");
      return;
    }
    setShowKeywordError(false);

    // Normalize and validate URL if provided
    let normalizedWebsite = website;
    if (website && website.trim()) {
      const validation = validateUrl(website);
      if (validation.isValid && validation.normalized) {
        normalizedWebsite = validation.normalized;
      } else if (!validation.isValid) {
        setWebsiteError(validation.error || "");
        log.warn("Form submission blocked: invalid URL");
        return;
      }
    }

    // Process peopleAlsoAskRaw before submission to ensure latest value is included
    const processedPeopleAlsoAsk = peopleAlsoAskRaw
      .split(/\n+/)
      .map((q) => q.trim())
      .filter((q) => q.length > 0);
    processPeopleAlsoAsk(peopleAlsoAskRaw);

    log.info("Form submitted");
    await onSubmit({
      website: normalizedWebsite,
      contentType,
      clientInfo,
      keyword,
      relatedKeywords,
      peopleAlsoAsk: processedPeopleAlsoAsk,
      title,
    });

    log.debug("Refreshing user details after content generation");
    await refreshUserDetails();
  };

  const handleReset = () => {
    setWebsite("");
    setContentType?.("");
    setClientInfo?.("");
    setKeyword("");
    setRelatedKeywords([]);
    setPeopleAlsoAsk?.([]);
    setPeopleAlsoAskRaw("");
    setTitle?.("");
    setWebsiteError("");
    setShowKeywordError(false);
    setSelectedClientId("");
    setSelectedWebsiteId("");
    onReset?.();
  };

  const handleClientChange = (clientId: string) => {
    setSelectedClientId(clientId);
    setSelectedWebsiteId("");
    setWebsite("");
    setContentType?.("");
    // Set clientInfo to the selected client's name, or clear it if no client is selected
    if (clientId) {
      const selectedClient = clients.find((c) => c.id === clientId);
      if (selectedClient) {
        setClientInfo?.(selectedClient.name);
      }
    } else {
      setClientInfo?.("");
    }
  };

  const handlePresetWebsiteChange = (websiteId: string) => {
    setSelectedWebsiteId(websiteId);
    const website = websites.find((w) => w.id === websiteId);
    if (website) {
      setWebsite(website.website);
      setContentType?.(website.contentType);
      const client = clients.find((c) => c.id === website.clientId);
      if (client) {
        setClientInfo?.(client.name);
        setSelectedClientId(client.id);
      }
    }
  };

  const handleClearPreset = () => {
    setSelectedClientId("");
    setSelectedWebsiteId("");
    setWebsite("");
    setContentType?.("");
    setClientInfo?.("");
  };

  const processPeopleAlsoAsk = (rawValue: string) => {
    const processed = rawValue
      .split(/\n+/)
      .map((q) => q.trim())
      .filter((q) => q.length > 0);
    setPeopleAlsoAsk?.(processed);
  };

  const handleWebsiteInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setWebsite(value);
    setWebsiteError("");
  };

  const handleWebsiteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value.trim();
    if (value) {
      const validation = validateUrl(value);
      if (validation.isValid && validation.normalized) {
        setWebsite(validation.normalized);
        setWebsiteError("");
      } else if (!validation.isValid) {
        setWebsiteError(validation.error || "");
      }
    } else {
      setWebsiteError("");
    }
  };

  useEffect(() => {
    const formElements =
      document.querySelectorAll<HTMLFormElement>(".asw-keyword-cta");

    const syncInputs = (e: Event) => {
      const newValue = (e.target as HTMLInputElement).value;
      setKeyword(newValue);

      formElements.forEach((formElement) => {
        const inputElements =
          formElement.querySelectorAll<HTMLInputElement>("input[type='text']");
        inputElements.forEach((input) => {
          if (input !== e.target) {
            input.value = newValue;
          }
        });
      });
    };

    const handleScrollToApp = () => {
      document.getElementById("asw")?.scrollIntoView({ behavior: "smooth" });
    };

    formElements.forEach((formElement) => {
      const inputElements =
        formElement.querySelectorAll<HTMLInputElement>("input[type='text']");
      const buttonElements = formElement.querySelectorAll<HTMLButtonElement>(
        "button[type='button']"
      );

      inputElements.forEach((input) => {
        input.addEventListener("input", syncInputs);
      });

      buttonElements.forEach((button) => {
        button.addEventListener("click", handleScrollToApp);
      });
    });

    return () => {
      formElements.forEach((formElement) => {
        const inputElements =
          formElement.querySelectorAll<HTMLInputElement>("input[type='text']");
        const buttonElements = formElement.querySelectorAll<HTMLButtonElement>(
          "button[type='button']"
        );

        inputElements.forEach((input) => {
          input.removeEventListener("input", syncInputs);
        });

        buttonElements.forEach((button) => {
          button.removeEventListener("click", handleScrollToApp);
        });
      });
    };
  }, [setKeyword]);

  useEffect(() => {
    const formElements =
      document.querySelectorAll<HTMLFormElement>(".asw-keyword-cta");

    formElements.forEach((formElement) => {
      const inputElements =
        formElement.querySelectorAll<HTMLInputElement>("input[type='text']");
      inputElements.forEach((input) => {
        input.value = keyword;
      });
    });
  }, [keyword]);

  // Sync peopleAlsoAskRaw with peopleAlsoAsk when it changes externally
  useEffect(() => {
    if (peopleAlsoAsk && peopleAlsoAsk.length > 0) {
      setPeopleAlsoAskRaw(peopleAlsoAsk.join("\n"));
    } else {
      setPeopleAlsoAskRaw("");
    }
  }, [peopleAlsoAsk]);

  return (
    <form
      onSubmit={handleSubmit}
      className={`grid gap-4 w-full shadow-lg p-6 bg-base-100 rounded-tl-[60px] rounded-br-[60px] transition-all xl:p-12 ${
        variant === "edit" ? "sm:w-1/3" : ""
      }`}
    >
      {variant === "edit" && (
        <>
          <h2 className="!text-4xl !font-medium text-primary !mb-0">
            Migliora il risultato
          </h2>
          <div className="divider"></div>
        </>
      )}
      <div className="mb-4">
        <h3 className="mb-2 text-primary !text-2xl !font-medium">
          Dove verrà inserito il tuo testo?
        </h3>
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="preset-client" className="text-sm font-medium text-left">
              Cliente (opzionale)
            </label>
            <select
              id="preset-client"
              value={selectedClientId}
              onChange={(e) => handleClientChange(e.target.value)}
              className="w-full rounded-2xl select select-bordered"
            >
              <option value="">Nessun preset – inserisci manualmente</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          </div>
          {selectedClientId && (
            <div className="flex flex-col gap-2">
              <label htmlFor="preset-website" className="text-sm font-medium text-left">
                Sito web (opzionale)
              </label>
              <select
                id="preset-website"
                value={selectedWebsiteId}
                onChange={(e) => handlePresetWebsiteChange(e.target.value)}
                className="w-full rounded-2xl select select-bordered"
              >
                <option value="">Nessun preset – inserisci manualmente</option>
                {websites.map((website) => (
                  <option key={website.id} value={website.id}>
                    {website.website} - {website.contentType}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isPresetSelected && (
            <button
              type="button"
              onClick={handleClearPreset}
              className="self-start px-4 text-sm rounded-full btn btn-outline btn-sm"
            >
              Rimuovi preset
            </button>
          )}
        </div>
        <div
          className={`flex flex-col gap-4 ${
            variant === "create" ? "sm:flex-row" : ""
          }`}
        >
          <div className={variant === "create" ? "sm:w-1/3" : "w-full"}>
            <input
              id="website"
              type="text"
              value={website}
              onChange={handleWebsiteInputChange}
              onBlur={handleWebsiteBlur}
              readOnly={isPresetSelected}
              className={`w-full border rounded-2xl input ${
                websiteError
                  ? "border-error"
                  : "border-gray-300"
              } ${isPresetSelected ? "bg-gray-100 cursor-not-allowed" : ""}`}
              placeholder="URL del sito web"
            />
            {websiteError && (
              <p className="mt-1 text-sm text-error">{websiteError}</p>
            )}
          </div>
          <input
            id="contentType"
            type="text"
            value={contentType || ""}
            onChange={(e) => setContentType?.(e.target.value)}
            readOnly={isPresetSelected}
            className={`w-full border border-gray-300 input rounded-2xl ${
              variant === "create" ? "sm:w-1/3" : ""
            } ${isPresetSelected ? "bg-gray-100 cursor-not-allowed" : ""}`}
            placeholder="Tipo di contenuto (es. medico-sanitario)"
          />
          {!selectedClientId && (
            <input
              id="clientInfo"
              type="text"
              value={clientInfo || ""}
              onChange={(e) => setClientInfo?.(e.target.value)}
              className={`w-full border border-gray-300 input rounded-2xl ${
                variant === "create" ? "sm:w-1/3" : ""
              }`}
              placeholder="Cliente (es. nome e/o descrizione del cliente)"
            />
          )}
        </div>
      </div>

      <div className="mb-4">
        <h3 className="mb-2 text-primary !text-2xl !font-medium">
          Titolo di lavoro
        </h3>
        <input
          id="title"
          type="text"
          value={title || ""}
          onChange={(e) => setTitle?.(e.target.value)}
          className="w-full rounded-2xl border border-gray-300 input"
          placeholder="Inserisci un titolo provvisorio (facoltativo)"
        />
      </div>

      <div className="">
        <h3 className="mb-2 text-primary !text-2xl !font-medium">
          Quali sono le keyword del contenuto?
          <span className="ml-1 text-error">*</span>
        </h3>
        <input
          id="keyword"
          type="text"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            if (showKeywordError && e.target.value.trim()) {
              setShowKeywordError(false);
            }
          }}
          className="w-full rounded-2xl border border-gray-300 input"
          placeholder="Inserisci la keyword principale su cui ti vuoi posizionare"
          required
        />
        {showKeywordError && (
          <p className="mt-1 text-sm text-error">
            Campo obbligatorio
          </p>
        )}
      </div>


      <div className="mb-4">
        <div className="flex flex-wrap gap-2 items-center py-3 mb-2 w-full h-auto rounded-2xl border border-gray-300 input">
          <KeywordInput
            keywords={relatedKeywords}
            setKeywords={setRelatedKeywords}
            placeholder="Inserisci le keyword correlate separate da una virgola"
          />
        </div>
      </div>

      <div className="mb-4">
        <h3 className="mb-2 text-primary !text-2xl !font-medium">
          People Also Ask
        </h3>
        <textarea
          id="people-also-ask"
          value={peopleAlsoAskRaw}
          onChange={(e) => setPeopleAlsoAskRaw(e.target.value)}
          onBlur={() => processPeopleAlsoAsk(peopleAlsoAskRaw)}
          className="w-full min-h-[120px] border border-gray-300 textarea rounded-2xl"
          placeholder="Inserisci le domande correlate, una per riga"
        />
      </div>

      <div
        className={`flex flex-col-reverse gap-4 sm:justify-between ${
          variant === "create" ? "sm:flex-row" : ""
        }`}
      >
        <button
          type="button"
          className="px-6 text-base bg-black bg-opacity-30 rounded-full border-none text-primary-content btn"
          onClick={handleReset}
        >
          <ResetIcon />
          Reimposta
        </button>
        <button
          type="submit"
          className="px-6 text-base rounded-full border-none text-primary-content bg-primary btn"
          disabled={isButtonDisabled}
        >
          <PencilIcon />
          {isLoading ? (
            <>Sto scrivendo…</>
          ) : variant === "create" ? (
            "Genera contenuto"
          ) : (
            "Rigenera contenuto"
          )}
        </button>
      </div>

      {isLoading ? (
        <div className="overflow-hidden relative w-full h-2 bg-gray-300 rounded-full">
          <div className="h-full bg-primary animate-progress"></div>
        </div>
      ) : (
        ""
      )}

      {(!userDetails ||
        (userDetails && userDetails.remainingContents !== Infinity)) && (
        <div className="mt-6 text-xl font-medium">
          {userDetails ? (
            userDetails.remainingContents &&
            userDetails.remainingContents > 0 ? (
              `Questo mese puoi generare ancora ${userDetails.remainingContents} contenuti SEO.`
            ) : (
              <>
                Questo mese hai già generato 4 contenuti SEO.
                <Link to="/checkout" className="text-primary hover:underline">
                  Passa al Premium Plan
                </Link>
                .
              </>
            )
          ) : (
            "Prova il piano gratuito, puoi creare fino a 4 contenuti SEO al mese."
          )}
        </div>
      )}
    </form>
  );
};

export default ContentForm;
